import type { ReactNode } from "react";
import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  createCommand,
  type LexicalCommand,
  type LexicalNode,
} from "lexical";
import {
  $createVariableNode,
  $isVariableNode,
  type EditorVariable,
} from "./VariableNode";
import { VariableContext } from "./VariableContext";

export const INSERT_VARIABLE_COMMAND: LexicalCommand<string> = createCommand(
  "INSERT_VARIABLE_COMMAND",
);

interface Props {
  available: ReadonlyArray<EditorVariable>;
  children?: ReactNode;
}

function siblingBefore(): LexicalNode | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
  const anchor = selection.anchor;
  const node = anchor.getNode();
  if ($isTextNode(node)) {
    if (anchor.offset !== 0) return null;
    return node.getPreviousSibling();
  }
  if ($isElementNode(node)) {
    return node.getChildAtIndex(anchor.offset - 1) || null;
  }
  return null;
}

function siblingAfter(): LexicalNode | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
  const anchor = selection.anchor;
  const node = anchor.getNode();
  if ($isTextNode(node)) {
    if (anchor.offset !== node.getTextContentSize()) return null;
    return node.getNextSibling();
  }
  if ($isElementNode(node)) {
    return node.getChildAtIndex(anchor.offset) || null;
  }
  return null;
}

export default function VariablePlugin({ available, children }: Props) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const unregisterInsert = editor.registerCommand(
      INSERT_VARIABLE_COMMAND,
      (variableKey: string) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;
        $insertNodes([$createVariableNode(variableKey)]);
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );

    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const target = siblingBefore();
        if (!$isVariableNode(target)) return false;
        event?.preventDefault();
        target.remove();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    const unregisterDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        const target = siblingAfter();
        if (!$isVariableNode(target)) return false;
        event?.preventDefault();
        target.remove();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      unregisterInsert();
      unregisterBackspace();
      unregisterDelete();
    };
  }, [editor]);

  return (
    <VariableContext.Provider value={{ available }}>
      {children}
    </VariableContext.Provider>
  );
}
